import React, {
  FunctionComponent,
  useEffect,
  useState,
  useCallback,
} from 'react';
import { orderBy, cloneDeep, isEmpty } from 'lodash';
import ReactPaginate from 'react-paginate';
import moment from 'moment';
import { Dealer } from '@upstart/harmony/lib/legos/dealerTypes';
import { CrmType, TradeInType } from '@upstart/harmony/lib/constants';
import './LendingStatus.scss';
import LendingStatusTable from './LendingStatusTable';
import { DefaultPaginationConfig } from './DefaultPaginationConfig';

interface Props {
  dealers: {
    dealers: Array<Dealer>;
    fetching: boolean;
  };
  groups: {
    groups: Array<{
      id: string;
      name?: string;
    }>;
  };
  actions: {
    dealersRequest: Function;
    groupsRequest: Function;
  };
}

interface PaginationChange {
  selected: number;
}

interface GroupsMapType {
  [index: string]: string | null | undefined;
}

const LendingStatus: FunctionComponent<Props> = ({
  dealers: { dealers, fetching },
  groups: { groups },
  actions,
}) => {
  // dealers to display on the page (pagination)
  const [displayedDealerList, setDisplayedDealerList] = useState<Dealer[]>([]);
  // dealers after using filters
  const [filteredDealerList, setFilteredDealerList] = useState<Dealer[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState(
    cloneDeep(DefaultPaginationConfig),
  );
  // number of pages
  const [pageCount, setPageCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  // sort asc/desc state
  const [sortByAsc, setSortByAsc] = useState(true);
  // sort by which column
  const [sorter, setSorter] = useState('name');
  const [groupsMap, setGroupsMap] = useState<GroupsMapType>({});
  const daysConsideredDelayed = 3;

  const handleRenderStatus = useCallback((dealer) => {
    // Has dealer logged in within 3 days, if not, they are delayed
    const delayedDealerDate = moment().subtract(daysConsideredDelayed, 'd');
    if (!dealer.launchpad?.firstLogin) {
      return STATUS.NOT_STARTED;
    }
    if (dealer.liveAt) {
      return STATUS.LAUNCHED;
    }
    if (isNeedsReviewStatus(dealer)) {
      return STATUS.NEEDS_REVIEW;
    }
    if (dealer.launchpad.status?.readyToLaunch) {
      return STATUS.READY_FOR_LAUNCH;
    }
    if (moment(dealer.launchpad.lastLogin) > delayedDealerDate) {
      return STATUS.IN_PROGRESS;
    }
    return STATUS.DELAYED;
  }, []);

  const extraAttributes = [
    'launchpad',
    'lending',
    'liveAt',
    'crmType',
    'dealersocket',
    'address',
    'timezone',
    'taxes',
    'brand',
    'featureConfig',
    'tradeInManagerType',
    'firstlook',
  ];

  useEffect(() => {
    actions.groupsRequest();
    actions.dealersRequest({
      addExtraInformation: false,
      extraAttributes,
    });
  }, []);

  // update total dealers based on the filter
  useEffect(() => {
    const filteredDealers = [];
    // only need to show the dealers created by launchpad
    const totalDealers = dealers.filter((dealer) => dealer.launchpad);

    for (const dealer of totalDealers || []) {
      if (searchTerm) {
        const searchTermLC = searchTerm.toLowerCase();
        const { name, launchpad } = dealer;
        const email = launchpad?.email || '';
        if (
          name?.toLowerCase().includes(searchTermLC) ||
          email.toLowerCase().includes(searchTermLC)
        ) {
          filteredDealers.push(dealer);
        }
      } else {
        filteredDealers.push(dealer);
      }
    }
    setFilteredDealerList(filteredDealers);
    setPageCount(Math.ceil(filteredDealers.length / pagination.limit));
    setCurrentPage(0);
  }, [dealers, searchTerm, pagination.limit]);

  useEffect(() => {
    if (groups.length > 0) {
      const groupsMapObj: GroupsMapType = {};
      groups.forEach(({ id, name }) => {
        groupsMapObj[id] = name;
      });

      setGroupsMap(groupsMapObj);
    }
  }, [groups]);

  // update display dealer list
  useEffect(() => {
    let displayedDealers = [];

    switch (sorter) {
      case 'obtainStatus':
        displayedDealers = orderBy(
          filteredDealerList,
          (dealer) => handleRenderStatus(dealer)?.label,
          sortByAsc ? 'asc' : 'desc',
        );
        break;

      case 'lendingStatus':
        displayedDealers = orderBy(
          filteredDealerList,
          (dealer) =>
            dealer.lending?.online?.enabled ||
            dealer.lending?.instore?.enabled ||
            false,
          sortByAsc ? 'asc' : 'desc',
        );
        break;

      case 'name':
        displayedDealers = orderBy(
          filteredDealerList,
          (dealer) => (dealer.name ? dealer.name.toLowerCase() : ''),
          sortByAsc ? 'asc' : 'desc',
        );
        break;

      case 'launchpad.firstLogin':
        displayedDealers = orderBy(
          filteredDealerList,
          (dealer) =>
            dealer.launchpad?.firstLogin
              ? moment(dealer.launchpad.firstLogin)
              : '',
          sortByAsc ? 'asc' : 'desc',
        );
        break;

      case 'group':
        displayedDealers = orderBy(
          filteredDealerList,
          (dealer) =>
            dealer.dealergroup_id && groupsMap[dealer.dealergroup_id]
              ? groupsMap[dealer.dealergroup_id]?.toLowerCase()
              : '',
          sortByAsc ? 'asc' : 'desc',
        );
        break;

      case 'upstartloans':
        displayedDealers = orderBy(
          filteredDealerList,
          (dealer) => dealer.lending?.upstart?.enabled || false,
          sortByAsc ? 'asc' : 'desc',
        );
        break;

      default:
        displayedDealers = orderBy(
          filteredDealerList,
          sorter,
          sortByAsc ? 'asc' : 'desc',
        );
        break;
    }

    const base = currentPage * pagination.limit;
    setDisplayedDealerList(
      displayedDealers.slice(base, base + pagination.limit),
    );
  }, [sorter, sortByAsc, filteredDealerList, currentPage]);

  const handleSort = useCallback(
    (newSorter) => () => {
      // If the sorter is the same, change the order
      if (sorter === newSorter) {
        setSortByAsc(!sortByAsc);
      } else {
        setSorter(newSorter);
      }
    },
    [sorter, sortByAsc],
  );

  const handlePaginationChange = (page: PaginationChange) => {
    const selected = page.selected;
    const offset = Math.ceil(selected * pagination.limit);
    const current = offset / pagination.limit;
    pagination.offset = offset;
    pagination.current = current;
    setCurrentPage(current);
    setPagination(pagination);
  };

  return (
    <div className="lending-status-container">
      <div className="lending-status-dashboard-header">
        <h1>Launch Status</h1>
        <div className="lending-status-search-box">
          <input
            type="text"
            className="form-control input"
            name="lendingStatusSearch"
            placeholder="Try: Dealer Name, Email"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>
      <LendingStatusTable
        dealersList={displayedDealerList}
        isFetching={fetching}
        sortBy={handleSort}
        renderStatus={handleRenderStatus}
        searchTerm={searchTerm}
        groupsMap={groupsMap}
      />
      {filteredDealerList.length > pagination.limit && (
        <div className="pagination-container">
          <div className="result-count-info" id="resultCountInfo">
            <span className="pagination-showing-label">Showing</span>
            {pagination.offset + 1} -{' '}
            {pagination.offset + displayedDealerList.length} of{' '}
            {filteredDealerList.length}
          </div>
          <ReactPaginate
            previousLabel="&laquo;"
            nextLabel="&raquo;"
            breakLabel="..."
            breakClassName="break-me"
            pageCount={pageCount}
            marginPagesDisplayed={1}
            pageRangeDisplayed={2}
            onPageChange={handlePaginationChange}
            containerClassName="pagination"
            activeClassName="active"
            initialPage={currentPage}
            disableInitialCallback
          />
        </div>
      )}
    </div>
  );
};

export const STATUS = {
  NOT_STARTED: {
    label: 'Not started',
    color: '#CCC',
  },
  LAUNCHED: {
    label: 'Launched',
    color: '#0765F9',
  },
  NEEDS_REVIEW: {
    label: 'Needs Review',
    color: '#EE1A30',
  },
  READY_FOR_LAUNCH: {
    label: 'Ready For Launch',
    color: '#0CCA58',
  },
  IN_PROGRESS: {
    label: 'In Progress',
    color: '#8F07F9',
  },
  DELAYED: {
    label: 'Delayed',
    color: '#F97B07',
  },
};

export const isNeedsReviewStatus = (dealer: Dealer) => {
  // Inventory provider needs to be set up (Dealer has provided the inventory provider but no feeds are configured)
  if (!!dealer.launchpad?.inventory?.provider && !dealer.Feeds?.length)
    return true;

  // Feeds are configured, however there are no cars imported yet. An Inventory Import should be needed
  if (!!dealer.launchpad?.inventory?.provider && !!dealer.Feeds?.length)
    return true;

  // DealerSocket Id & Site number need to be configured (Dealer has selected DealerSocket as their CRM but these fields are not configured)
  if (
    dealer.crmType === CrmType.DEALERSOCKET &&
    (!dealer.dealersocket?.dealersocketId ||
      !dealer.dealersocket?.dealersocketSiteNumber)
  )
    return true;

  // Dealer timezone could not be automatically filled in, the address may need to be verified
  if (!!dealer.address && !dealer.timezone) return true;

  // Dealer taxes could not be automatically filled in and need to be manually set
  if (!!dealer.address && isEmpty(dealer.taxes)) return true;

  // Shift integration should be enabled for Subaru and Toyota dealers
  if (
    !!(dealer.brand?.includes('Subaru') || dealer.brand?.includes('Toyota')) &&
    !dealer.featureConfig?.shiftDigital?.enabled
  )
    return true;

  // Unite integration should be enabled for Mitsubishi dealers
  if (
    !!dealer.brand?.includes('Mitsubishi') &&
    !dealer.featureConfig?.unite?.enabled
  )
    return true;

  // FirstLook needs a dealer id and passcode configured
  if (
    dealer.tradeInManagerType === TradeInType.FIRSTLOOK &&
    (!dealer.firstlook?.dealerId || !dealer.firstlook.passcode)
  )
    return true;

  return false;
};

export default LendingStatus;
